// FilteredPosts.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSubscription, useQuery, gql } from '@apollo/client';
import HistorialIcon from './icons/historial';
import './historial.css';

const FETCH_POSTS_QUERY_USER = gql`
  query getPostsByUsername($username: String!, $idUserlink: String!) {
    getPostsByUsername(username: $username, idUserlink: $idUserlink) {
      id
      body
      estado
      createdAt
      username
      likeCount
      likes { username }
      commentCount
      comments {
        id
        body
        respuesta
        createdAt
        username
      }
    }
  }
`;

const NEW_POST_SUBSCRIPTION = gql`
  subscription {
    newPost {
      id
      body
      estado
      createdAt
      username
      idUserlink
      comments {
        id
        body
        respuesta
        createdAt
        username
      }
      likes { username }
    }
  }
`;

const FilteredPosts = ({ user, idUserlink, selectedPostId }) => {
  const username = (user || '').replace(/[^a-zA-Z0-9]/g, '');

  const [posts, setPosts] = useState([]);
  const [highlightedPostId, setHighlightedPostId] = useState(null);

  const { data, loading, error } = useQuery(FETCH_POSTS_QUERY_USER, {
    variables: { username, idUserlink },
    skip: !username || !idUserlink
  });

  // Ordenador por fecha desc
  const sortByCreatedDesc = (arr) =>
    [...arr].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  // Cargar inicial y ordenar
  useEffect(() => {
    if (data?.getPostsByUsername) {
      setPosts(sortByCreatedDesc(data.getPostsByUsername));
    }
  }, [data]);

  // Subscriptions: merge/update y mantener orden
  useSubscription(NEW_POST_SUBSCRIPTION, {
    onData: ({ data }) => {
      const newPost = data?.data?.newPost;
      if (!newPost) return;

      const match =
        (username && newPost.username === username) ||
        (idUserlink && newPost.idUserlink === idUserlink);

      if (!match) return;

      setPosts((prev) => {
        const idx = prev.findIndex((p) => p.id === newPost.id);
        if (idx !== -1) {
          const current = prev[idx];
          if (current.body !== newPost.body || current.createdAt !== newPost.createdAt) {
            const updated = [...prev];
            updated[idx] = { ...current, ...newPost };
            setHighlightedPostId(newPost.id);
            setTimeout(() => setHighlightedPostId(null), 2000);
            return sortByCreatedDesc(updated);
          }
          return prev;
        }
        // Nuevo post → al tope (y reordenar por fecha por si vino con timestamp)
        const next = sortByCreatedDesc([newPost, ...prev]);
        setHighlightedPostId(newPost.id);
        setTimeout(() => setHighlightedPostId(null), 2000);
        return next;
      });
    }
  });

  // Scroll al post activo cuando cambia
  useEffect(() => {
    if (!selectedPostId) return;
    const el = document.querySelector(`.post-link[data-id="${selectedPostId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedPostId, posts.length]);

  if (error) {
    return (
      <div className="spinner-container">
        <div className="spinner">
          <img src="/images/logo512.png" alt="loading" />
        </div>
      </div>
    );
  }

  return (
    <div className="historial-wrapper">
      {loading ? (
        <div className="spinner-container">
          <div className="spinner">
            <img src="/images/logo512.png" alt="loading" />
          </div>
        </div>
      ) : (
        <div className="posts-container fade-bottom">
          {posts.map((post, index) => {
            // Opacidad suave por índice (además del fade del contenedor)
            const opacity = Math.max(0.35, 1 - index * 0.015); // ajustable
            const text =
              (post.body || '').length > 19
                ? post.body.substring(0, 19).toLowerCase() + '...'
                : (post.body || '').toLowerCase();

            return (
              <div key={post.id} className="post-item" style={{ opacity }}>
                <Link
                  to={{ pathname: `/chat/${post.id}`, state: {} }}
                  className={[
                    'post-link',
                    String(post.id) === String(selectedPostId) ? 'active' : '',
                    highlightedPostId === post.id ? 'highlighted' : ''
                  ].join(' ')}
                  data-id={post.id}
                  title={post.body}
                >
                  <HistorialIcon size={13} />
                  {' ' + text}
                  <span className="created-at">
                    {new Date(post.createdAt).toLocaleString()}
                  </span>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilteredPosts;
